// =============================================================================
// src/data/ai-surfaces/copilot-surfaces.ts — Superfícies de IA do Copiloto.
//
// Sidebar (#aiCopilot) + chat takeover (#copilotChatTakeover). Campos ancorados em
//   código real: buildCopilotOverlay (src/data/sentinel-overlay.ts) e
//   insights-client.ts#toInsight. Ver AI-CONTRACT.md (gerado deste registro).
// =============================================================================

import type { AiSurfaceEntry } from '../ai-surface-types';

export const COPILOT_AI_SURFACES: readonly AiSurfaceEntry[] = [
  {
    id: 'copiloto.sidebar-context',
    label: 'Contexto atual (Copiloto)',
    domain: 'copiloto',
    location: 'Sidebar #aiCopilot · painel "Contexto atual"',
    renderRef: 'src/chrome/copilot-sidebar/insight-panel-controller.ts#writeInsight',
    aiKind: 'insight',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.section_insight' },
    status: 'live',
    inputs: ['insight por seção (24h)', 'série eleitoral 2014–2024', 'socioeconômico'],
    fields: [
      { uiField: 'copilotHeadline', backendField: 'headline', type: 'string' },
      { uiField: 'copilotDetail', backendField: 'detail', type: 'string' },
      { uiField: 'copilotConfidence', backendField: 'confidence', type: 'percent' },
    ],
  },
  {
    id: 'copiloto.sidebar-why',
    label: 'Por quê & ressalvas',
    domain: 'copiloto',
    location: 'Sidebar #aiCopilot · #copilotWhy',
    renderRef: 'src/chrome/copilot-sidebar/insight-panel-controller.ts#writeAction',
    aiKind: 'insight',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.section_insight' },
    status: 'live',
    inputs: ['metodologia do insight', 'evidence_ids'],
    fields: [{ uiField: 'copilotWhy', backendField: 'why', type: 'string' }],
  },
  {
    id: 'copiloto.sidebar-tactics',
    label: 'Oportunidades táticas',
    domain: 'copiloto',
    location: 'Sidebar #aiCopilot · #copilotTactics',
    renderRef: 'src/chrome/copilot-sidebar/tactic-cards-by-section.ts#TACTIC_CARDS_BY_SECTION',
    aiKind: 'recommendation',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.ai_action_recommendation' },
    status: 'live',
    inputs: ['recomendações por seção', 'impacto eleitoral estimado'],
    fields: [
      { uiField: 'tacticTitle', backendField: 'title', type: 'string' },
      { uiField: 'tacticConfidence', backendField: 'confidence', type: 'percent' },
      { uiField: 'tacticImpact', backendField: 'recommended_actions[].impact', type: 'string' },
    ],
  },
  {
    id: 'copiloto.chat-narrative',
    label: 'Resposta do chat (narrativa)',
    domain: 'copiloto',
    location: 'Takeover #copilotChatTakeover · parágrafos da resposta',
    renderRef: 'src/features/copilot-chat/copilot-answers.ts#buildAnswer',
    aiKind: 'chat',
    backendLayer: 'camada-3.5',
    backendSource: { kind: 'fastapi-endpoint', name: '/copilot/client/{id}/chat' },
    status: 'partial',
    inputs: ['pergunta do usuário', 'snapshot 24h Sentinel', 'intent'],
    fields: [
      { uiField: 'answerText', backendField: 'paragraphs', type: 'string[]' },
      { uiField: 'answerModel', backendField: 'model', type: 'string' },
    ],
  },
  {
    id: 'copiloto.chat-card-indice',
    label: 'Card Índice de reputação',
    domain: 'copiloto',
    location: 'Chat · card "Índice de reputação"',
    renderRef: 'src/features/copilot-chat/copilot-cards-core.ts#cardIndice',
    aiKind: 'aggregate',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_summary' },
    status: 'live',
    inputs: ['sentimento ponderado 24h', 'share of voice', 'protagonismo'],
    fields: [
      { uiField: 'irm.value', backendField: 'reputation_index', type: 'number' },
      { uiField: 'irm.subdims[Percepção]', backendField: 'nrs', type: 'number' },
      { uiField: 'irm.subdims[Presença]', backendField: 'sov', type: 'number' },
      { uiField: 'irm.subdims[Protagonismo]', backendField: 'bps', type: 'number' },
    ],
  },
  {
    id: 'copiloto.chat-card-sentimento',
    label: 'Card Sentimento 24h',
    domain: 'copiloto',
    location: 'Chat · card "Sentimento"',
    renderRef: 'src/features/copilot-chat/copilot-cards-core.ts#cardSentimento',
    aiKind: 'sentiment',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_sentiment_24h' },
    status: 'live',
    inputs: ['classificação NLP por item (MiniMax M3)'],
    fields: [
      { uiField: 'sentimento.neg', backendField: 'neg_pct', type: 'percent' },
      { uiField: 'sentimento.neu', backendField: 'neu_pct', type: 'percent' },
      { uiField: 'sentimento.pos', backendField: 'pos_pct', type: 'percent' },
      { uiField: 'sentimento.total', backendField: 'total', type: 'number' },
      { uiField: 'sentimento.foot', backendField: 'avg_weighted_score', type: 'number', transform: 'toFixed(2)' },
    ],
  },
  {
    id: 'copiloto.chat-card-feed',
    label: 'Card Feed de menções',
    domain: 'copiloto',
    location: 'Chat · card "Feed"',
    renderRef: 'src/features/copilot-chat/copilot-cards-extra.ts#cardFeed',
    aiKind: 'classification',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_feed_recent' },
    status: 'live',
    inputs: ['menções coletadas', 'sentimento + ameaça por item'],
    fields: [
      { uiField: 'feed[].time', backendField: 'published_at', type: 'iso-datetime', transform: 'formatTime' },
      { uiField: 'feed[].src', backendField: 'author_name', type: 'string' },
      { uiField: 'feed[].sent', backendField: 'sent_class', type: 'enum', transform: 'mapSentClass' },
      { uiField: 'feed[].threat', backendField: 'threat_level', type: 'number', transform: 'clamp 1..5' },
      { uiField: 'feed[].text', backendField: 'body_preview', type: 'string', transform: 'slice(0,180)' },
    ],
  },
  {
    id: 'copiloto.chat-card-diagnostico',
    label: 'Card Diagnóstico',
    domain: 'copiloto',
    location: 'Chat · card "Diagnóstico"',
    renderRef: 'src/features/copilot-chat/copilot-cards-extra.ts#cardDiagnostico',
    aiKind: 'recommendation',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.ai_action_recommendation' },
    status: 'live',
    inputs: ['recomendação de ação', 'alerta como fallback'],
    fields: [
      { uiField: 'diagnostico.contexto', backendField: 'title', type: 'string' },
      { uiField: 'diagnostico.impacto', backendField: 'summary', type: 'string' },
      { uiField: 'diagnostico.leitura', backendField: 'risk_level+confidence', type: 'string' },
    ],
  },
  {
    id: 'copiloto.chat-card-plano',
    label: 'Card Plano de resposta',
    domain: 'copiloto',
    location: 'Chat · card "Plano"',
    renderRef: 'src/features/copilot-chat/copilot-plan.ts#buildPlanCard',
    aiKind: 'recommendation',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.ai_action_recommendation' },
    status: 'live',
    inputs: ['recommended_actions', 'janela temporal'],
    fields: [
      { uiField: 'plano[].when', backendField: 'recommended_actions[].when', type: 'string' },
      { uiField: 'plano[].text', backendField: 'recommended_actions[].text', type: 'string' },
      { uiField: 'plano[].tags', backendField: 'recommended_actions[].tags', type: 'string[]' },
    ],
  },
  {
    id: 'copiloto.chat-card-evolucao',
    label: 'Card Evolução (90 dias)',
    domain: 'copiloto',
    location: 'Chat · card "Evolução"',
    renderRef: 'src/features/copilot-chat/copilot-cards-extra.ts#cardEvolucao',
    aiKind: 'forecast',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_trend' },
    status: 'live',
    inputs: ['série diária do índice de reputação'],
    fields: [
      { uiField: 'trend[].value', backendField: 'reputation_index', type: 'number' },
      { uiField: 'trend[].label', backendField: 'ts', type: 'iso-datetime', transform: 'formatTrendLabel' },
    ],
  },
  {
    id: 'copiloto.chat-card-threat',
    label: 'Card Nível de ameaça',
    domain: 'copiloto',
    location: 'Chat · card "Nível de ameaça"',
    renderRef: 'src/features/copilot-chat/copilot-cards-core.ts#cardThreat',
    aiKind: 'classification',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-table', name: 'monitoring.content_analysis' },
    status: 'mock',
    inputs: ['crisis_score por item', 'volume/aceleração'],
    fields: [
      { uiField: 'threatGeral.level', backendField: 'crisis_score', type: 'number', transform: 'escala 1..5' },
    ],
  },
  {
    id: 'copiloto.chat-card-sov',
    label: 'Card Share of Voice',
    domain: 'copiloto',
    location: 'Chat · card "Share of Voice"',
    renderRef: 'src/features/copilot-chat/copilot-cards-extra.ts#cardShareOfVoice',
    aiKind: 'aggregate',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_reputation_summary' },
    status: 'partial',
    inputs: ['share of voice do cliente', 'menções dos concorrentes'],
    fields: [{ uiField: 'sov.pct', backendField: 'sov', type: 'percent' }],
  },
  {
    id: 'copiloto.chat-card-volume-canal',
    label: 'Card Volume por canal',
    domain: 'copiloto',
    location: 'Chat · card "Volume por canal"',
    renderRef: 'src/features/copilot-chat/copilot-cards-extra.ts#cardVolumeCanal',
    aiKind: 'aggregate',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_feed_recent' },
    status: 'mock',
    inputs: ['menções agrupadas por plataforma'],
    fields: [{ uiField: 'canal[].count', backendField: 'platform (group by)', type: 'number' }],
  },
  {
    id: 'copiloto.chat-card-linha-tempo',
    label: 'Card Linha do tempo',
    domain: 'copiloto',
    location: 'Chat · card "Linha do tempo"',
    renderRef: 'src/features/copilot-chat/copilot-cards-extra.ts#cardLinhaTempo',
    aiKind: 'forecast',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'monitoring.topic_signal' },
    status: 'mock',
    inputs: ['ciclo de vida do tópico', 'fato/boato/enquadramento'],
    fields: [
      { uiField: 'linhaTempo[].periodo', backendField: 'period_start', type: 'iso-datetime' },
      { uiField: 'linhaTempo[].volume', backendField: 'content_count', type: 'number' },
    ],
  },
];
